import { Injectable, inject } from '@angular/core';
import { IgniteResponse } from '@clubhub/ignite/models';
import { FilterKey, IIgniteFilterOption } from '@clubhub/ignite/models/affordability';
import { IgniteCommonDataService, IgniteService } from '@clubhub/ignite/services';
import { SavedSearchCommonDataService } from '@clubhub/ignite/services/saved-searches/saved-search-common-data.service';
import { AffordabilityMet, YesNoResults } from '@shared/models';
import { OutcomeResults } from '@shared/models/enums/outcome-results';
import { SortOrder } from '@shared/models/filter-sort';
import { AnalyticsService } from '@shared/services/analytics.service';
import orderBy from 'lodash-es/orderBy';

@Injectable({
  providedIn: 'root',
})
export class ResultsFilterService {
  private readonly igniteService = inject(IgniteService);
  private readonly igniteCommonDataService = inject(IgniteCommonDataService);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly savedSearchCommonDataService = inject(SavedSearchCommonDataService);

  public filter: IIgniteFilterOption = {};

  /**
   * The broker's saved Affordability search preferences, kept apart from the live filters so they survive the
   * screen resetting itself.
   *
   * Not merged into `filters` on read. A saved search replays by assigning `filter` wholesale, and a merge would
   * reintroduce a preference for a slot that was deliberately unset when that search was saved. Seeding on reset
   * gets the same result without touching replay.
   */
  private preferredFilter: IIgniteFilterOption = {};

  /**
   * The subset of the above the broker's firm has locked, which "clear filters" must leave standing.
   *
   * Held separately rather than recomputed, because the locks are resolved once per search and the button has no
   * access to them.
   */
  private lockedFilter: IIgniteFilterOption = {};

  public get filters(): Readonly<IIgniteFilterOption> {
    return this.filter;
  }

  /**
   * Applies the broker's saved preferences, and remembers them for subsequent resets.
   *
   * Does not mark the search as having unsaved changes: a preference is not something the broker just did, and
   * Evidence of Results auto-saves a search that is dirty. Anything already set wins, since it was set for this
   * search rather than being a default.
   */
  public setPreferredFilters(preferred: IIgniteFilterOption, locked: readonly string[] = []): void {
    this.preferredFilter = { ...preferred };
    this.lockedFilter = Object.fromEntries(
      Object.entries(preferred).filter(([key]) => locked.includes(key)),
    ) as IIgniteFilterOption;
    this.filter = { ...preferred, ...this.filter };
  }

  /**
   * Forgets the preferences this service is holding, so the next `setPreferredFilters` is the authority.
   *
   * Called only when the broker has actually saved new preferences. This service is `providedIn: 'root'` and so
   * outlives a search - navigating away from Ignite destroys components, not services. On the next search
   * `resetFilter` re-seeds from what is held here, and `setPreferredFilters` then lets anything already set win,
   * so without this the values from the previous search would beat the ones just saved.
   *
   * Deliberately leaves `filter` alone. Doing this on every visit to the preferences page, rather than on a
   * save, wiped filters the broker had set for a search they were still editing.
   */
  public forgetPreferredFilters(): void {
    this.preferredFilter = {};
    this.lockedFilter = {};
  }

  public get hasFilter(): boolean {
    return !!(
      this.filter.lenderName ??
      this.filter.outcomeResult ??
      this.filter.igniteResultsOrder ??
      this.filter.propertyResult ??
      this.filter.productsMatched ??
      this.filter.affordabilityMatched
    );
  }

  /**
   * Clears the live filters back to the broker's saved preferences.
   *
   * Called when the outcomes screen mounts, so it means "start this screen fresh" rather than "the broker cleared
   * their filters" - and a fresh screen is exactly where a saved preference should apply.
   */
  public resetFilter(): void {
    this.filter = { ...this.preferredFilter };
  }

  /**
   * Clears the filters the broker chose, keeping only the ones their firm has locked.
   *
   * Distinct from <see cref="resetFilter" /> despite the similar shape. That one means "start this screen
   * fresh", where a saved preference is exactly what should apply. This is the broker saying "show me
   * everything", and answering it by putting their preferences back is not clearing anything - but a locked
   * filter is not theirs to clear either, so those stay.
   */
  public clearFilters(): void {
    this.savedSearchCommonDataService.clearSavedSearch();
    this.filter = { ...this.lockedFilter };
  }

  public onFilterChange(key: FilterKey, value: unknown): void {
    this.filter = { ...this.filter, [key]: value };
    let filterType: string | undefined = undefined;
    let details: string | undefined = undefined;
    this.savedSearchCommonDataService.clearSavedSearch();

    if ([FilterKey.ProductsMatched, FilterKey.AffordabilityMatched].includes(key)) {
      return;
    }

    if (key === FilterKey.OutcomeResult || key === FilterKey.PropertyResult) {
      switch (key) {
        case FilterKey.OutcomeResult:
          filterType = 'Criteria';
          break;
        case FilterKey.PropertyResult:
          filterType = 'Property';
          break;
      }

      if (value === OutcomeResults.Yes) {
        details = 'Yes';
      } else if (value === OutcomeResults.No) {
        details = 'No';
      } else if (value === OutcomeResults.Refer) {
        details = 'Refer';
      } else if (value === OutcomeResults.Unset) {
        details = 'Restricted';
      } else {
        details = 'Unset';
      }
    }

    if (details !== undefined) {
      this.analyticsService.trackDropdown(`Select ${filterType} Result Type: ${details}`, details);
    }
  }

  public get criteriaOnly() {
    return this.igniteCommonDataService.criteriaOnly;
  }

  public get propertyOnly() {
    return this.igniteCommonDataService.propertyOnly;
  }

  public filterResults(): IgniteResponse[] | null {
    let results = this.igniteService.getResults(); //ordered results
    if (!this.hasFilter || !results?.length) {
      return results;
    }

    const { lenderName, affordabilityMatched, igniteResultsOrder, outcomeResult, propertyResult, productsMatched } = this.filter;

    if (lenderName) {
      results = lenderName.length
        ? results.filter((o: IgniteResponse) => {
          return lenderName.some(x => x.toUpperCase() === o.lenderName.toUpperCase());
        })
        : results;
    }

    if (affordabilityMatched) {
      results = results.filter((result: IgniteResponse) => {
        const isErrorOrOther =
          result.affordabilitySearchId?.length &&
          (result.httpError || result.error?.message) &&
          !this.igniteCommonDataService.isNetFreePayTooLow(result);

        if (affordabilityMatched === AffordabilityMet.Yes) {
          return this.igniteCommonDataService.isAffordabilityMet(result) && !isErrorOrOther;
        }
        if (affordabilityMatched === AffordabilityMet.No) {
          return !this.igniteCommonDataService.isAffordabilityMet(result) && !this.criteriaOnly && !this.propertyOnly && !isErrorOrOther;
        }
        if (affordabilityMatched === AffordabilityMet.Unknown) {
          return (!this.igniteCommonDataService.isAffordabilityMet(result) && (this.criteriaOnly || this.propertyOnly)) || !!isErrorOrOther;
        }

        return false;
      });
    }

    if (outcomeResult) {
      results = results.filter(
        (result: IgniteResponse) =>
          this.igniteService.getCriteriaFilterResult(result) === (outcomeResult === OutcomeResults.Unset ? undefined : outcomeResult),
      );
    }

    if (propertyResult) {
      results = results.filter(
        (result: IgniteResponse) =>
          this.igniteService.getPropertyFilterResult(result) === (propertyResult === OutcomeResults.Unset ? undefined : propertyResult),
      );
    }

    if (productsMatched) {
      let resultsWithLenderProducts = results
        .map(result => {
          const lenderProducts = this.igniteService.getLenderMatchedProducts(result);
          return {
            lenderProducts: lenderProducts,
            ...result,
          };
        })
        .filter(result => {
          return productsMatched === YesNoResults.Yes ? result.lenderProducts?.products?.length : !result.lenderProducts?.products?.length;
        });

      if (productsMatched === YesNoResults.No) {
        resultsWithLenderProducts = resultsWithLenderProducts.sort(r => (!r.lenderProducts ? 1 : -1));
      }

      results = resultsWithLenderProducts;
    }

    if (igniteResultsOrder) {
      results = orderBy(
        results,
        // The fallback value for max value depends on the sort order, this way results with no data always appear last
        (o: IgniteResponse) => o.maximumAffordableLoanAmount ?? (igniteResultsOrder === SortOrder.Ascending ? Number.MAX_VALUE : -1),
        igniteResultsOrder === SortOrder.Ascending ? 'asc' : 'desc',
      );
    }

    return results;
  }
}
